import { useFormik } from 'formik';
import emailjs from 'emailjs-com';
import { useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Stack } from '@mui/material';
import * as Yup from 'yup';

const backdropSx = {
  backgroundColor: 'rgba(10, 22, 40, 0.55)',
  backdropFilter: 'blur(8px)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: "'DM Sans', sans-serif",
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'background-color 0.15s ease',
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
    '& fieldset': {
      borderColor: 'rgba(10, 22, 40, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(30, 107, 184, 0.35)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1e6bb8',
      borderWidth: '1px',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    color: '#5c6b7a',
  },
  '& .MuiInputBase-inputMultiline': {
    paddingTop: '10px',
  },
};

const labelProps = { shrink: true };

function ContactModal({ setToggleModal }) {
  const [success, setSuccess] = useState(false);

  const schema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    institution: Yup.string().required('Required'),
    email: Yup.string().required('Required').email('Invalid email address'),
    message: Yup.string().required('Required'),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      institution: '',
      email: '',
      message: '',
    },
    validationSchema: schema,
    validateOnMount: true,
    onSubmit: (values, { setSubmitting }) => {
      emailjs
        .send('service_uq1sl4n', 'template_sys9jl8', values, 'Cmj9VpwpnB_UROGkx')
        .then(() => {
          setSuccess(true);
        })
        .finally(() => setSubmitting(false));
    },
  });

  function closeAll() {
    setSuccess(false);
    setToggleModal();
  }

  return (
    <Modal
      open
      onClose={closeAll}
      aria-labelledby="vivo-contact-title"
      BackdropProps={{ sx: backdropSx }}
    >
      <Box
        className="vivo-contact-modal"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(480px, calc(100vw - 24px))',
          maxHeight: 'calc(100vh - 24px)',
          overflow: 'auto',
          bgcolor: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 24px 48px rgba(10, 22, 40, 0.18), 0 0 0 1px rgba(10, 22, 40, 0.06)',
          fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 3,
            pt: 3,
            pb: success ? 2 : 1,
            borderBottom: success ? 'none' : '1px solid rgba(10, 22, 40, 0.06)',
          }}
        >
          <Typography
            id="vivo-contact-title"
            component="h2"
            sx={{
              fontSize: '1.35rem',
              fontWeight: 700,
              color: '#0a1628',
              letterSpacing: '-0.02em',
            }}
          >
            {success ? 'Thank you' : 'Contact'}
          </Typography>
          <button
            type="button"
            className="vivo-contact-modal__close"
            onClick={closeAll}
            aria-label="Close"
          >
            ×
          </button>
        </Box>

        {success ? (
          <Box sx={{ px: 3, pb: 3, pt: 2 }}>
            <Typography sx={{ color: '#5c6b7a', fontSize: '0.95rem', lineHeight: 1.6, mb: 2.5 }}>
              Your message is on its way. Someone from the VIVO SURGERY team will follow up shortly.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={closeAll}
              sx={{
                py: 1.25,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                background: 'linear-gradient(135deg, #1e6bb8, #12b4c8)',
                boxShadow: '0 4px 16px rgba(30, 107, 184, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a5da3, #0fa3b5)',
                  boxShadow: '0 6px 20px rgba(30, 107, 184, 0.38)',
                },
              }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ px: 3, pb: 3, pt: 2 }}
            noValidate
          >
            <Stack spacing={2.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  name="firstName"
                  label="First name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  InputLabelProps={labelProps}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  name="lastName"
                  label="Last name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  InputLabelProps={labelProps}
                  sx={fieldSx}
                />
              </Stack>
              <TextField
                fullWidth
                size="small"
                name="institution"
                label="Organization"
                value={formik.values.institution}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.institution && Boolean(formik.errors.institution)}
                helperText={formik.touched.institution && formik.errors.institution}
                InputLabelProps={labelProps}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                name="email"
                label="Email"
                type="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                InputLabelProps={labelProps}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                name="message"
                label="Message"
                placeholder="How can we help?"
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
                InputLabelProps={labelProps}
                sx={fieldSx}
              />
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
              <Button
                type="button"
                variant="text"
                onClick={closeAll}
                sx={{
                  color: '#5c6b7a',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '999px',
                  px: 2,
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!formik.isValid || formik.isSubmitting}
                sx={{
                  px: 2.5,
                  py: 1,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1e6bb8, #12b4c8)',
                  boxShadow: '0 4px 14px rgba(30, 107, 184, 0.28)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a5da3, #0fa3b5)',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(10, 22, 40, 0.12)',
                    color: 'rgba(10, 22, 40, 0.35)',
                  },
                }}
              >
                Send
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default ContactModal;
